import { defineComponent, reactive, ref, watch, computed } from 'vue'
import { validateImage } from '@/helpers/validateImage'
import { useBannersStore } from '@/store/banners'
import { useCatalogStore } from '@/store/catalog'
import { useServicesStore } from '@/store/services'
import { useAppStore } from '@/store/app/app'
import AddImage from '@/components/files/AddImage/AddImage.vue'
import type { PropType } from 'vue'
import type { IBanner, UpdateBannerPayload } from '@/types/banners'
import type { SelectItem } from '@/types/catalog'
import type { DefaultError } from '@/types/httpError'

interface BannerFields {
  title: string
  categoryId: number | string
  serviceCategoryId: number | string
  image: {
    preview: string
    value: File | null
  }
  isActive: boolean
}

export default defineComponent({
  name: 'HomeBannerForm',
  props: {
    banner: {
      type: Object as PropType<IBanner>,
    },
  },
  components: {
    AddImage,
  },
  emits: ['close'],
  setup(props, { emit }) {
    const bannersStore = useBannersStore()
    const catalogStore = useCatalogStore()
    const servicesStore = useServicesStore()
    const appStore = useAppStore()

    const isProductsAvailable = computed(() => appStore.main?.hasMarket)
    const isServicesAvailable = computed(() => appStore.main?.hasServices)

    const isReqLoading = ref(false)
    const fields = reactive<BannerFields>({
      title: '',
      categoryId: '',
      serviceCategoryId: '',
      image: {
        preview: '',
        value: null,
      },
      isActive: true,
    })
    const selectedProductCategory = ref<SelectItem | ''>('')
    const selectedServiceCategory = ref<SelectItem | ''>('')
    const bannerId = ref<string | number>('')
    const error = ref('')
    const errorFields = ref<Record<string, string[]> | null>(null)
    const photoErrors = ref<string[] | null>(null)
    const categoryToSelect = ref<'products' | 'services' | ''>(
      isProductsAvailable.value ? 'products' : isServicesAvailable.value ? 'services' : '',
    )

    const productCategories = computed(() => catalogStore.parentCategories)
    const servicesCategories = computed(() => servicesStore.parentCategories)
    const isProductCategoriesLoading = computed(() => catalogStore.parentCategoriesLoading)
    const isServicesCategoriesLoading = computed(() => servicesStore.isParentCategoriesLoading)

    watch(
      () => props.banner,
      newVal => {
        if (newVal) {
          const {
            id,
            title,
            categoryId,
            serviceCategoryId,
            image: originalImage,
            isActive,
          } = newVal
          bannerId.value = id
          const image = {
            preview: originalImage?.previewUrl ?? '',
            value: null,
          }
          categoryToSelect.value = serviceCategoryId ? 'services' : 'products'
          Object.assign(fields, {
            title,
            categoryId: categoryId ?? '',
            serviceCategoryId: serviceCategoryId ?? '',
            image,
            isActive,
          })

          const categoryItem = productCategories.value?.find(c => c.id === categoryId)
          if (categoryItem)
            selectedProductCategory.value = categoryItem

          const serviceCategoryItem = servicesCategories.value?.find(
            c => c.id === serviceCategoryId,
          )
          if (serviceCategoryItem)
            selectedServiceCategory.value = serviceCategoryItem
        }
      },
      { immediate: true },
    )

    const onUpdateValue = (key: 'isActive' | 'categoryId', value: SelectItem | boolean) => {
      if (key === 'isActive') {
        fields[key] = value as boolean
        return
      }
      fields[key] = (value as SelectItem).id
    }

    const onCategoryToSelectUpdate = (key: string, value: 'products' | 'services') =>
      (categoryToSelect.value = value)

    const onFileUpload = (files: FileList) => {
      photoErrors.value = null

      if (!files || !files.length)
        return

      const file = files[0]
      const errors = validateImage(file, ['jpg', 'png', 'jpeg'])

      if (errors.length) {
        photoErrors.value = errors
        return
      }
      const link = URL.createObjectURL(file)

      fields.image = {
        preview: link,
        value: file,
      }
    }

    const onRemoveImage = () => {
      fields.image = {
        preview: '',
        value: null,
      }
    }

    const handleCreateBanner = async () => {
      isReqLoading.value = true
      bannersStore.error = null
      errorFields.value = null
      photoErrors.value = null

      const formData = new FormData()

      formData.append('title', fields.title)
      if (categoryToSelect.value === 'products') {
        formData.append('category_id', String(fields.categoryId))
      } else {
        formData.append('service_category_id', String(fields.serviceCategoryId))
      }

      if (fields.image.value) {
        formData.append('image', fields.image.value)
      }
      formData.append('is_active', fields.isActive ? '1' : '0')

      await bannersStore.createBanner(formData)
      isReqLoading.value = false

      if (bannersStore.error) {
        error.value = (bannersStore.error as DefaultError).error
        errorFields.value = (bannersStore.error as DefaultError).data
        return
      }

      emit('close')
    }

    const onImageUpdate = async (bannerId: number, image: File) => {
      const formData = new FormData()
      formData.append('image', image)

      await bannersStore.updateBannerImage({ id: bannerId, data: formData })
    }

    const handleUpdateBanner = async () => {
      isReqLoading.value = true
      bannersStore.error = null
      errorFields.value = null
      photoErrors.value = null
      const newImage = fields.image.value
      const id = +bannerId.value
      if (newImage) {
        await onImageUpdate(id, newImage)
      }

      if (bannersStore.error) {
        error.value = (bannersStore.error as DefaultError).error
        isReqLoading.value = false
        return
      }

      const { title, categoryId, serviceCategoryId, isActive } = fields

      const body: UpdateBannerPayload['data'] = {
        title,
        isActive,
      }
      if (categoryToSelect.value === 'products') {
        body.categoryId = +categoryId
        body.serviceCategoryId = null
      } else {
        body.serviceCategoryId = +serviceCategoryId
        body.categoryId = null
      }

      await bannersStore.updateBanner({
        id,
        data: body,
      })

      isReqLoading.value = false

      if (bannersStore.error) {
        error.value = (bannersStore.error as DefaultError).error
        errorFields.value = (bannersStore.error as DefaultError).data
        return
      }

      emit('close')
    }

    const submitHandler = computed(() => (props.banner ? handleUpdateBanner : handleCreateBanner))

    const isSubmitBtnDisabled = computed(
      () =>
        !fields.title
        || !fields.image.preview
        || (categoryToSelect.value === 'products' && !fields.categoryId)
        || (categoryToSelect.value === 'services' && !fields.serviceCategoryId),
    )

    const productCategoriesSearchChange = async (value: string) =>
      await catalogStore.getParentCategories(value)
    const serviceCategoriesSearchChange = async (value: string) =>
      await servicesStore.getParentCategories(value)

    return {
      isReqLoading,
      fields,
      selectedProductCategory,
      selectedServiceCategory,
      error,
      errorFields,
      photoErrors,
      productCategories,
      servicesCategories,
      categoryToSelect,
      onUpdateValue,
      onFileUpload,
      onRemoveImage,
      onCategoryToSelectUpdate,
      submitHandler,
      isSubmitBtnDisabled,
      isProductCategoriesLoading,
      isServicesCategoriesLoading,
      isProductsAvailable,
      isServicesAvailable,
      productCategoriesSearchChange,
      serviceCategoriesSearchChange,
    }
  },
})
